import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as Actions from '../redux/actions';
import Autocomplete from '../components/Autocomplete';
import AdminCalculatedTests from './AdminCalculatedTests';
import useBackdropClose from '../utils/useBackdropClose';
import '../styles/admin.scss';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

// Calculated tests are still being built: hidden unless REACT_APP_CALCULATED_TESTS=true
// (always shown in development). The API's routes for them are off the same way.
const CALCULATED_TESTS = process.env.REACT_APP_CALCULATED_TESTS === 'true' || process.env.NODE_ENV === 'development';

const FILTERS = [
  { id: 'all', label: 'Active' },
  { id: 'staff', label: 'Staff' },
  { id: 'operator', label: 'Operators' },
  { id: 'attention', label: 'Needs attention' },
  { id: 'deactivated', label: 'Deactivated' },
];

const EMPTY_USER = {
  type: 'staff',
  username: '',
  email: '',
  first_name: '',
  last_name: '',
  employee_id: '',
  customer: null,
};

const EMPTY_PASSWORD = { mode: 'type', password: '', temppass: true };

const EMPTY_ADDRESS = { street: '', city: '', state: '', zip: '', phone: '', description: '' };

// An employee's address from the API (or null) as form values.
const addressForm = (address) => Object.fromEntries(Object.keys(EMPTY_ADDRESS).map((k) => [k, address?.[k] || '']));

// Position 1 is Technician.
const EMPTY_EMPLOYEE = {
  first_name: '',
  last_name: '',
  position_id: '1',
  managed_by: '',
  address: EMPTY_ADDRESS,
};

const matchesFilter = (user, filter) => {
  switch (filter) {
    case 'staff': return user.type === 'staff' && !user.deactivated_at;
    case 'operator': return user.type === 'operator' && !user.deactivated_at;
    case 'attention': return user.warnings.length > 0;
    case 'deactivated': return !!user.deactivated_at;
    default: return !user.deactivated_at;
  }
};

const passwordPayload = (pw) => (
  pw.mode === 'generate'
    ? { generate_password: true, temppass: pw.temppass }
    : { password: pw.password, temppass: pw.temppass }
);

const PasswordFields = ({ value, onChange }) => (
  <div className="admin-password-fields">
    <div className="admin-segmented" role="group" aria-label="Password source">
      <button
        type="button"
        className={value.mode === 'type' ? 'active' : ''}
        onClick={() => onChange({ ...value, mode: 'type' })}
      >
        Type a password
      </button>
      <button
        type="button"
        className={value.mode === 'generate' ? 'active' : ''}
        onClick={() => onChange({ ...value, mode: 'generate', password: '' })}
      >
        Generate one
      </button>
    </div>
    {value.mode === 'type' && (
      <input
        className="admin-input browser-default"
        type="text"
        autoComplete="new-password"
        placeholder="6–30 characters"
        value={value.password}
        onChange={(e) => onChange({ ...value, password: e.target.value })}
      />
    )}
    <label className="admin-checkbox">
      <input
        type="checkbox"
        className="browser-default"
        checked={value.temppass}
        onChange={(e) => onChange({ ...value, temppass: e.target.checked })}
      />
      <span>Must change password at next login</span>
    </label>
  </div>
);

const AddressFields = ({ value, onChange, lastName }) => {
  const set = (field) => (e) => onChange({ ...value, [field]: e.target.value });
  return (
    <div className="admin-grid">
      <label className="admin-field">
        <span>Phone</span>
        <input className="admin-input browser-default" type="tel" value={value.phone} onChange={set('phone')} />
      </label>
      <label className="admin-field">
        <span>Description</span>
        <input
          className="admin-input browser-default"
          placeholder={lastName ? `${lastName} Residence` : 'e.g. Smith Residence'}
          value={value.description}
          onChange={set('description')}
        />
      </label>
      <label className="admin-field wide">
        <span>Street</span>
        <input className="admin-input browser-default" value={value.street} onChange={set('street')} />
      </label>
      <label className="admin-field">
        <span>City</span>
        <input className="admin-input browser-default" value={value.city} onChange={set('city')} />
      </label>
      <label className="admin-field">
        <span>State</span>
        <input className="admin-input browser-default" maxLength={2} value={value.state} onChange={set('state')} />
      </label>
      <label className="admin-field">
        <span>Zip</span>
        <input className="admin-input browser-default" inputMode="numeric" value={value.zip} onChange={set('zip')} />
      </label>
    </div>
  );
};

const GeneratedPassword = ({ password }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div className="admin-generated">
      <span className="admin-generated-label">Password (shown only once)</span>
      <code className="admin-generated-value">{password}</code>
      <button
        type="button"
        className="admin-btn ghost"
        onClick={() => navigator.clipboard.writeText(password).then(() => setCopied(true))}
      >
        <ContentCopyIcon /> {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
};

const UserEditor = ({ user, employees, positions, accounts, currentUserId, api, onSaved, onClose }) => {
  const isNew = !user.id;
  const [form, setForm] = useState(() => ({
    ...EMPTY_USER,
    ...user,
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    employee_id: user.employee?.id ? `${user.employee.id}` : '',
  }));
  const [customerLabel, setCustomerLabel] = useState(user.customer?.name || '');
  const [password, setPassword] = useState(EMPTY_PASSWORD);
  const [sendInvite, setSendInvite] = useState(false);
  // New staff get a new employee record unless an existing one is picked.
  const [employeeMode, setEmployeeMode] = useState('new'); // new | existing
  const [newEmployee, setNewEmployee] = useState(EMPTY_EMPLOYEE);
  // The linked employee's address, editable once the user exists.
  const [employeeAddress, setEmployeeAddress] = useState(() => addressForm(user.employee?.address));
  const backdrop = useBackdropClose(onClose);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [generated, setGenerated] = useState(null);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const setEmployee = (field) => (e) => setNewEmployee({ ...newEmployee, [field]: e.target.value });

  // Keeps the editor in step with the user the API returned.
  const loadSaved = (saved) => {
    setForm((f) => ({ ...f, ...saved, employee_id: saved.employee?.id ? `${saved.employee.id}` : '' }));
    setEmployeeAddress(addressForm(saved.employee?.address));
  };

  const run = (key, request, onSuccess) => {
    setBusy(key);
    setError(null);
    setNotice(null);
    return request
      .then(onSuccess)
      .catch((e) => setError(e.message))
      .finally(() => setBusy(null));
  };

  const created = !isNew || !!form.id;
  const creatingEmployee = !created && form.type === 'staff' && employeeMode === 'new';
  // The address belongs to the saved employee, so it waits while a different one is picked.
  const savedEmployeeId = form.employee?.id ? `${form.employee.id}` : '';
  const editingAddress = created && form.type === 'staff' && !!savedEmployeeId && form.employee_id === savedEmployeeId;

  const profile = {
    username: form.username,
    email: form.email,
    first_name: form.first_name,
    last_name: form.last_name,
    ...(form.type === 'operator' ? { customer_id: form.customer?.id }
      : creatingEmployee ? { new_employee: newEmployee }
      : { employee_id: form.employee_id }),
  };

  const save = () => {
    if (isNew) {
      run('save', api.create({
        type: form.type,
        ...profile,
        ...passwordPayload(password),
        send_invite: form.type === 'operator' && sendInvite,
      }), (json) => {
        onSaved(json.user);
        loadSaved(json.user);
        setGenerated(json.password);
        setPassword(EMPTY_PASSWORD);
        setNotice(`Created ${json.user.username}.${form.type === 'operator' && sendInvite ? ' Invite emailed.' : ''}`);
      });
    } else {
      run('save', api.update({ id: form.id, ...profile, ...(editingAddress && { employee_address: employeeAddress }) }), (json) => {
        onSaved(json.user);
        loadSaved(json.user);
        setNotice('Saved.');
      });
    }
  };

  const setUserPassword = () => {
    run('password', api.setPassword({ id: form.id, ...passwordPayload(password) }), (json) => {
      onSaved(json.user);
      setGenerated(json.password);
      setPassword(EMPTY_PASSWORD);
      setNotice('Password updated.');
    });
  };

  const toggleActive = () => {
    const deactivating = !form.deactivated_at;
    if (deactivating && !window.confirm(`Deactivate ${form.username}? They will be signed out of Wave and unable to log in.`)) return;
    run('active', deactivating ? api.deactivate(form.id) : api.reactivate(form.id), (json) => {
      onSaved(json.user);
      setForm((f) => ({ ...f, deactivated_at: json.user.deactivated_at }));
    });
  };

  const employeeOptions = employees.map((e) => {
    const linkedElsewhere = e.user_ids.some((id) => id !== form.id);
    return (
      <option key={e.id} value={e.id} disabled={linkedElsewhere}>
        {e.name}{e.position ? ` — ${e.position}` : ''}{e.active ? '' : ' (inactive)'}{linkedElsewhere ? ' (linked)' : ''}
      </option>
    );
  });

  const accountList = (accounts || []).map((a) => ({ id: a.id, label: a.name }));

  return (
    <div className="admin-modal-backdrop" {...backdrop} role="presentation">
      <div className="admin-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={isNew ? 'New user' : `Edit ${user.username}`}>
        <header className="admin-modal-head">
          <div>
            <h2 className="admin-modal-title">{created ? form.username : 'New user'}</h2>
            {created && (
              <p className="admin-modal-subtitle">
                {form.type === 'operator' ? `Operator · ${form.customer?.name || ''}` : 'Staff'}
                {form.deactivated_at ? ' · Deactivated' : ''}
              </p>
            )}
          </div>
          <button type="button" className="admin-icon-btn" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </header>

        {notice && <div className="admin-notice">{notice}</div>}
        {error && <div className="admin-error">{error}</div>}
        {generated && <GeneratedPassword password={generated} />}

        {!created && (
          <div className="admin-segmented" role="group" aria-label="Account type">
            <button type="button" className={form.type === 'staff' ? 'active' : ''} onClick={() => setForm({ ...form, type: 'staff' })}>Staff</button>
            <button type="button" className={form.type === 'operator' ? 'active' : ''} onClick={() => setForm({ ...form, type: 'operator' })}>Operator</button>
          </div>
        )}

        <section className="admin-section">
          <div className="admin-grid">
            <label className="admin-field">
              <span>Username</span>
              <input className="admin-input browser-default" autoCapitalize="none" value={form.username} onChange={set('username')} />
            </label>
            <label className="admin-field">
              <span>Email</span>
              <input className="admin-input browser-default" type="email" autoCapitalize="none" value={form.email} onChange={set('email')} />
            </label>
            {/* Staff names come from the linked employee record */}
            {form.type === 'operator' && (
              <>
                <label className="admin-field">
                  <span>First name</span>
                  <input className="admin-input browser-default" value={form.first_name} onChange={set('first_name')} />
                </label>
                <label className="admin-field">
                  <span>Last name</span>
                  <input className="admin-input browser-default" value={form.last_name} onChange={set('last_name')} />
                </label>
              </>
            )}

            {form.type === 'staff' && !created && (
              <div className="admin-field wide">
                <span>Employee record</span>
                <div className="admin-segmented" role="group" aria-label="Employee record">
                  <button type="button" className={employeeMode === 'new' ? 'active' : ''} onClick={() => setEmployeeMode('new')}>Create new employee</button>
                  <button type="button" className={employeeMode === 'existing' ? 'active' : ''} onClick={() => setEmployeeMode('existing')}>Link existing employee</button>
                </div>
              </div>
            )}

            {creatingEmployee ? (
              <>
                <label className="admin-field">
                  <span>First name</span>
                  <input className="admin-input browser-default" value={newEmployee.first_name} onChange={setEmployee('first_name')} />
                </label>
                <label className="admin-field">
                  <span>Last name</span>
                  <input className="admin-input browser-default" value={newEmployee.last_name} onChange={setEmployee('last_name')} />
                </label>
                <label className="admin-field">
                  <span>Position</span>
                  <select className="admin-input browser-default" value={newEmployee.position_id} onChange={setEmployee('position_id')}>
                    {(positions || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </label>
                <label className="admin-field">
                  <span>Manager (optional)</span>
                  <select className="admin-input browser-default" value={newEmployee.managed_by} onChange={setEmployee('managed_by')}>
                    <option value="">None</option>
                    {employees.filter((e) => e.active).map((e) => (
                      <option key={e.id} value={e.id}>{e.name}{e.position ? ` — ${e.position}` : ''}</option>
                    ))}
                  </select>
                </label>
              </>
            ) : form.type === 'staff' ? (
              <label className="admin-field wide">
                <span>Employee (name comes from here)</span>
                <select className="admin-input browser-default" value={form.employee_id} onChange={set('employee_id')}>
                  <option value="">Select an employee…</option>
                  {employeeOptions}
                </select>
              </label>
            ) : (
              <div className="admin-field wide">
                <span>Customer</span>
                <Autocomplete
                  list={accountList}
                  value={customerLabel}
                  onChange={(label) => {
                    setCustomerLabel(label);
                    if (form.customer && label !== form.customer.name) setForm({ ...form, customer: null });
                  }}
                  onSelect={(a) => setForm({ ...form, customer: { id: a.id, name: a.label } })}
                  placeholder="Search customers…"
                  searchIcon
                />
              </div>
            )}
          </div>
        </section>

        {creatingEmployee && (
          <section className="admin-section">
            <h3 className="admin-section-title">Employee address (optional)</h3>
            <AddressFields
              value={newEmployee.address}
              onChange={(address) => setNewEmployee({ ...newEmployee, address })}
              lastName={newEmployee.last_name}
            />
          </section>
        )}

        {editingAddress && (
          <section className="admin-section">
            <h3 className="admin-section-title">Employee address</h3>
            <AddressFields
              value={employeeAddress}
              onChange={setEmployeeAddress}
              lastName={form.employee.name.split(' ').pop()}
            />
          </section>
        )}
        {created && form.type === 'staff' && form.employee_id && !editingAddress && (
          <p className="admin-empty">Save to edit the newly linked employee's address.</p>
        )}

        {!created && (
          <section className="admin-section">
            <h3 className="admin-section-title">Password</h3>
            <PasswordFields value={password} onChange={setPassword} />
            {form.type === 'operator' && (
              <label className="admin-checkbox">
                <input type="checkbox" className="browser-default" checked={sendInvite} onChange={(e) => setSendInvite(e.target.checked)} />
                <span>Email the operator an invite with their username and password</span>
              </label>
            )}
          </section>
        )}

        <div className="admin-actions">
          <button type="button" className="admin-btn primary" disabled={!!busy} onClick={save}>
            {busy === 'save' ? 'Saving…' : created ? 'Save changes' : 'Create user'}
          </button>
        </div>

        {created && (
          <>
            <section className="admin-section">
              <h3 className="admin-section-title">Change password</h3>
              <PasswordFields value={password} onChange={setPassword} />
              <div className="admin-actions">
                <button type="button" className="admin-btn" disabled={!!busy} onClick={setUserPassword}>
                  {busy === 'password' ? 'Updating…' : 'Set password'}
                </button>
              </div>
            </section>

            {form.id !== currentUserId && (
              <section className="admin-section">
                <h3 className="admin-section-title">Access</h3>
                <div className="admin-actions start">
                  <button
                    type="button"
                    className={`admin-btn ${form.deactivated_at ? '' : 'danger'}`}
                    disabled={!!busy}
                    onClick={toggleActive}
                  >
                    {form.deactivated_at ? 'Reactivate account' : 'Deactivate account'}
                  </button>
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const AdminUsers = ({
  authToken, user: currentUser, accounts, users: storedUsers, employees = [], employeePositions, showNav, fetchAccounts,
  fetchUsers, createUser, updateUser, setUserPassword, deactivateUser, reactivateUser,
}) => {
  // The store can hold a partial list (one account's operators, from the
  // account page), so wait for the full fetch before showing it.
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const users = useMemo(() => (loaded ? Object.values(storedUsers || {}) : null), [loaded, storedUsers]);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState('users'); // users | calculated

  // The editor's requests, with the auth token filled in.
  const api = useMemo(() => ({
    create: (user) => createUser(user, authToken),
    update: (user) => updateUser(user, authToken),
    setPassword: (body) => setUserPassword(body, authToken),
    deactivate: (id) => deactivateUser(id, authToken),
    reactivate: (id) => reactivateUser(id, authToken),
  }), [authToken, createUser, updateUser, setUserPassword, deactivateUser, reactivateUser]);

  const load = useCallback(() => (
    fetchUsers(authToken)
      .then(() => setLoaded(true))
      .catch((e) => setLoadError(e.message))
  ), [fetchUsers, authToken]);

  useEffect(() => {
    showNav(true);
    fetchAccounts(authToken);
    load();
  }, [authToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // The saved user is already in the store. Refetch anyway so employee links
  // and "shared employee" warnings on other users stay accurate.
  const onSaved = () => load();

  const counts = useMemo(() => (
    Object.fromEntries(FILTERS.map((f) => [f.id, (users || []).filter((u) => matchesFilter(u, f.id)).length]))
  ), [users]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (users || [])
      .filter((u) => matchesFilter(u, filter))
      .filter((u) => !q || [u.username, u.email, u.name, u.employee?.name, u.customer?.name]
        .some((v) => (v || '').toLowerCase().includes(q)))
      .sort((a, b) => (a.name || a.username).localeCompare(b.name || b.username));
  }, [users, filter, query]);

  return (
    <div className="admin-dashboard">
      <div className="admin-page">
        {CALCULATED_TESTS && (
          <div className="admin-segmented admin-views" role="group" aria-label="Admin section">
            <button type="button" className={view === 'users' ? 'active' : ''} onClick={() => setView('users')}>User accounts</button>
            <button type="button" className={view === 'calculated' ? 'active' : ''} onClick={() => setView('calculated')}>Calculated tests</button>
          </div>
        )}

        {CALCULATED_TESTS && view === 'calculated' ? <AdminCalculatedTests /> : (
        <>
        <header className="admin-page-head">
          <div>
            <h1 className="admin-page-title">User accounts</h1>
            <p className="admin-page-subtitle">Create staff and operator logins, set passwords, and link staff to employees.</p>
          </div>
          <button type="button" className="admin-btn primary" onClick={() => setEditing({ ...EMPTY_USER })}>
            <AddIcon /> New user
          </button>
        </header>

        <div className="admin-toolbar">
          <div className="admin-segmented" role="group" aria-label="Filter users">
            {FILTERS.map((f) => (
              <button key={f.id} type="button" className={filter === f.id ? 'active' : ''} onClick={() => setFilter(f.id)}>
                {f.label} <span className="admin-count">{counts[f.id] || 0}</span>
              </button>
            ))}
          </div>
          <label className="admin-search">
            <SearchIcon className="admin-search-icon" aria-hidden="true" />
            <input
              type="search"
              className="admin-input browser-default"
              placeholder="Search name, username, email, employee, customer…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>
        </div>

        {loadError && <div className="admin-error">{loadError}</div>}
        {!users && !loadError && <p className="admin-empty">Loading users…</p>}
        {users && visible.length === 0 && <p className="admin-empty">No matching users.</p>}

        {visible.length > 0 && (
          <ul className="admin-user-list">
            {visible.map((u) => (
              <li key={u.id}>
                <button
                  type="button"
                  className={`admin-user-row ${u.deactivated_at ? 'deactivated' : ''}`}
                  onClick={() => setEditing(u)}
                >
                  <div className="admin-user-main">
                    <span className="admin-user-name">
                      {u.name}
                      {u.admin && <span className="admin-badge accent">Admin</span>}
                      {u.type === 'operator' && <span className="admin-badge">Operator</span>}
                      {u.deactivated_at && <span className="admin-badge muted">Deactivated</span>}
                      {u.temppass && !u.deactivated_at && <span className="admin-badge">Temporary password</span>}
                    </span>
                    <span className="admin-user-meta">{u.username} · {u.email}</span>
                  </div>
                  <div className="admin-user-link">
                    {u.type === 'operator' ? u.customer?.name : u.employee?.name}
                    {u.warnings.map((w) => (
                      <span key={w} className="admin-warning"><WarningAmberIcon /> {w}</span>
                    ))}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
        </>
        )}
      </div>

      {editing && (
        <UserEditor
          key={editing.id || 'new'}
          user={editing}
          employees={employees}
          positions={employeePositions}
          accounts={accounts}
          currentUserId={currentUser?.id}
          api={api}
          onSaved={onSaved}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
};

function mapStateToProps(state) {
  return {
    authToken: state.authToken,
    user: state.user,
    accounts: state.accounts,
    users: state.users,
    employees: state.employees,
    employeePositions: state.employeePositions,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(Actions, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(AdminUsers);
